import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { AlertCircle, RefreshCw } from "lucide-react"

interface ErrorDisplayProps {
  error: Error | null
  onRetry?: () => void
  title?: string
  className?: string
}

/**
 * Componente para exibir erros de API de forma consistente
 */
export function ErrorDisplay({
  error,
  onRetry,
  title = "Erro ao carregar dados",
  className,
}: ErrorDisplayProps) {
  if (!error) return null

  // Extrair informações úteis do erro
  const getErrorMessage = (error: Error) => {
    // Se for erro de rede
    if (error.message.includes("Network Error") || error.message.includes("fetch")) {
      return "Erro de conexão. Verifique sua conexão com a internet."
    }

    // Se for erro HTTP com status
    if (error.message.includes("404")) {
      return "Recurso não encontrado."
    }

    if (error.message.includes("401")) {
      return "Não autorizado. Faça login novamente."
    }

    if (error.message.includes("403")) {
      return "Acesso negado. Você não tem permissão para acessar este recurso."
    }

    if (error.message.includes("500")) {
      return "Erro interno do servidor. Tente novamente em alguns minutos."
    }

    // Erro genérico
    return error.message || "Ocorreu um erro inesperado."
  }

  const getErrorDetails = (error: Error) => {
    // Tentar extrair detalhes adicionais se disponíveis
    if ((error as any).response?.data?.message) {
      return (error as any).response.data.message
    }

    if ((error as any).response?.data?.error) {
      return (error as any).response.data.error
    }

    if ((error as any).response?.statusText) {
      return `Status: ${(error as any).response.status} - ${(error as any).response.statusText}`
    }

    return null
  }

  const errorMessage = getErrorMessage(error)
  const errorDetails = getErrorDetails(error)

  return (
    <Alert variant="destructive" className={className}>
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="space-y-2">
        <div>{errorMessage}</div>

        {errorDetails && (
          <div className="bg-destructive/10 rounded p-2 font-mono text-sm opacity-80">
            {errorDetails}
          </div>
        )}

        {onRetry && (
          <div className="pt-2">
            <Button variant="outline" size="sm" onClick={onRetry} className="gap-2">
              <RefreshCw className="h-3 w-3" />
              Tentar Novamente
            </Button>
          </div>
        )}
      </AlertDescription>
    </Alert>
  )
}
